import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowUpDown, Briefcase, Building, Cloud, CreditCard,
  Edit2, ExternalLink, FileText, Folder, Grid, Key, KeyRound, Landmark,
  LayoutList, Lock, Mail, MoreHorizontal, Plus, Search, Share2,
  ShoppingCart, Star, Terminal, Trash2, Users, Wifi, X,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/MainLayout';
import { categoriesApi } from '../api/categories';
import api from '../api/client';


// ─── Icon Map ────────────────────────────────────────────────────────────────
const ICON_MAP = {
  lock: Lock, landmark: Landmark, 'credit-card': CreditCard, mail: Mail,
  users: Users, briefcase: Briefcase, user: KeyRound, cloud: Cloud,
  'shopping-cart': ShoppingCart, terminal: Terminal, key: Key, wifi: Wifi,
  'file-text': FileText, building: Building, folder: Folder,
};

const ICONS_LIST = Object.keys(ICON_MAP);

const COLOR_OPTIONS = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899',
  '#0891b2', '#7c3aed', '#06b6d4', '#f97316', '#1d4ed8',
  '#ef4444', '#15803d', '#92400e', '#1e40af', '#6b7280',
];

const EXPIRY_OPTIONS = [
  { label: '1 hour',  value: () => new Date(Date.now() + 3600000).toISOString() },
  { label: '1 day',   value: () => new Date(Date.now() + 86400000).toISOString() },
  { label: '5 days',  value: () => new Date(Date.now() + 432000000).toISOString() },
  { label: '30 days', value: () => new Date(Date.now() + 2592000000).toISOString() },
  { label: '1 year',  value: () => new Date(Date.now() + 31536000000).toISOString() },
  { label: 'Permanent', value: () => null },
];

const PERMISSIONS = ['VIEW', 'COPY', 'EDIT', 'MANAGE'];

function CategoryIcon({ icon, color, size = 20 }) {
  const IconComp = ICON_MAP[icon] || Folder;
  return <IconComp size={size} color={color || '#3b82f6'} />;
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    ACTIVE: { label: 'Active', cls: 'badge-active' },
    EXPIRING_SOON: { label: 'Expiring Soon', cls: 'badge-warning' },
    EXPIRED: { label: 'Expired', cls: 'badge-danger' },
    REVOKED: { label: 'Revoked', cls: 'badge-muted' },
  };
  const { label, cls } = map[status] || { label: status, cls: 'badge-muted' };
  return <span className={`cat-badge ${cls}`}>{label}</span>;
}

// ─── Modals ───────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    // Lock body scroll on mobile
    document.body.classList.add('mobile-modal-open');
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.classList.remove('mobile-modal-open');
    };
  }, [onClose]);

  return (
    <div className="cat-drawer-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div className="cat-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="cat-drawer-header">
          <h2>{title}</h2>
          <button className="icon-button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="cat-drawer-body">
          {children}
        </div>
      </div>
    </div>
  );
}

function CategoryForm({ initial = {}, teams = [], onSubmit, onClose, loading, error }) {
  const [name, setName] = useState(initial.name || '');
  const [description, setDescription] = useState(initial.description || '');
  const [icon, setIcon] = useState(initial.icon || 'folder');
  const [color, setColor] = useState(initial.color || '#3b82f6');
  const [teamId, setTeamId] = useState(initial.team_id || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ name: name.trim(), description: description.trim(), icon, color, teamId: teamId || undefined });
  };

  return (
    <form onSubmit={handleSubmit} className="cat-form">
      <div className="cat-form-row">
        <label className="input-label" htmlFor="cat-name">Name *</label>
        <input
          id="cat-name"
          className="input-field"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Work Credentials"
          minLength={2}
          maxLength={80}
          required
          autoFocus
        />
      </div>

      <div className="cat-form-row">
        <label className="input-label" htmlFor="cat-desc">Description</label>
        <textarea
          id="cat-desc"
          className="input-field"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this category is for…"
          rows={2}
          maxLength={500}
          style={{ resize: 'vertical' }}
        />
      </div>

      <div className="cat-form-row">
        <label className="input-label" htmlFor="cat-color">Color</label>
        <div className="cat-color-input-row">
          <input
            id="cat-color"
            type="color"
            className="cat-native-color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            aria-label="Choose color"
          />
          <span className="cat-color-preview" style={{ background: color }}>{color}</span>
        </div>
      </div>

      <div className="cat-form-row">
        <label className="input-label">Icon</label>
        <div className="cat-icon-picker">
          {ICONS_LIST.map((ico) => {
            const Ic = ICON_MAP[ico];
            return (
              <button
                type="button"
                key={ico}
                className={`cat-icon-btn ${icon === ico ? 'selected' : ''}`}
                onClick={() => setIcon(ico)}
                aria-label={ico}
                title={ico}
              >
                <Ic size={16} color={color} />
              </button>
            );
          })}
        </div>
      </div>

      {teams.length > 0 && (
        <div className="cat-form-row">
          <label className="input-label" htmlFor="cat-team">Team (optional)</label>
          <select id="cat-team" className="input-field" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">Personal</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      )}

      {error && <p className="form-error">{error}</p>}

      <div className="cat-modal-footer">
        <button type="button" className="btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
        <button type="submit" className="btn-primary" disabled={loading || !name.trim()}>
          {loading ? 'Saving…' : (initial.id ? 'Save Changes' : 'Create Category')}
        </button>
      </div>
    </form>
  );
}

function DeleteModal({ category, categories, onClose, onConfirm, loading, error }) {
  const [moveToId, setMoveToId] = useState('');
  const [mode, setMode] = useState('move'); // 'move' | 'unlink'
  const hasItems = category.item_count > 0;

  return (
    <Modal title="Delete Category" onClose={onClose}>
      <div className="cat-delete-body">
        <div className="confirm-icon"><Trash2 size={22} color="var(--danger)" /></div>
        <p className="cat-delete-title">Delete <strong>{category.name}</strong>?</p>

        {hasItems ? (
          <>
            <div className="cat-delete-warning">
              <AlertTriangle size={16} color="var(--warning)" />
              <span>This category contains <strong>{category.item_count}</strong> encrypted item{category.item_count !== 1 ? 's' : ''}.</span>
            </div>
            <div className="cat-delete-options">
              <label className="cat-radio-row">
                <input type="radio" name="mode" value="move" checked={mode === 'move'} onChange={() => setMode('move')} />
                <span>Move items to another category</span>
              </label>
              {mode === 'move' && (
                <select className="input-field" value={moveToId} onChange={(e) => setMoveToId(e.target.value)}>
                  <option value="">— Select target category —</option>
                  {categories
                    .filter((c) => c.id !== category.id)
                    .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
              <label className="cat-radio-row">
                <input type="radio" name="mode" value="unlink" checked={mode === 'unlink'} onChange={() => setMode('unlink')} />
                <span>Delete category only (items remain, uncategorized)</span>
              </label>
            </div>
          </>
        ) : (
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>This category is empty. It will be permanently removed.</p>
        )}

        {error && <p className="form-error" style={{ marginTop: 12 }}>{error}</p>}
        <div className="cat-modal-footer">
          <button className="btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button
            className="btn-danger"
            disabled={loading || (hasItems && mode === 'move' && !moveToId)}
            onClick={() => onConfirm({
              moveToId: mode === 'move' ? moveToId : undefined,
              deleteCategoryOnly: mode === 'unlink',
            })}
          >
            {loading ? 'Deleting…' : 'Delete Category'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ShareModal({ category, onClose, onShare, loading, error }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permission, setPermission] = useState('VIEW');
  const [expiryKey, setExpiryKey] = useState('1 day');
  const [shares, setShares] = useState(null);
  const [sharesLoading, setSharesLoading] = useState(true);

  useEffect(() => {
    categoriesApi.getShares(category.id).then(setShares).finally(() => setSharesLoading(false));
  }, [category.id]);

  // Debounced user search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get('/users/search', { params: { query: searchQuery } });
        setSearchResults(data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    const expiryOpt = EXPIRY_OPTIONS.find((o) => o.label === expiryKey);
    const expiresAt = expiryOpt?.value() || null;
    await onShare({ recipientEmail: selectedUser.email, permission, expiresAt });
  };

  const handleRevoke = async (shareId) => {
    await categoriesApi.revokeShare(category.id, shareId);
    setShares((prev) => prev.filter((s) => s.id !== shareId));
  };

  return (
    <Modal title={`Share "${category.name}"`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="cat-form">
        {/* User Search */}
        <div className="cat-form-row">
          <label className="input-label">Search recipient</label>
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }} />
            <input
              className="input-field"
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedUser(null);
              }}
              placeholder="Type a name or email to search…"
              style={{ paddingLeft: '36px', marginBottom: 0 }}
              autoComplete="off"
            />
          </div>

          {/* Search results dropdown */}
          {searchQuery.trim() && !selectedUser && (
            <div style={{ marginTop: '6px', border: '1px solid var(--border-color)', borderRadius: '10px', overflow: 'hidden', maxHeight: '200px', overflowY: 'auto', background: 'var(--bg-secondary)' }}>
              {searching ? (
                <div style={{ padding: '12px 14px', color: 'var(--text-secondary)', fontSize: '13px' }}>Searching…</div>
              ) : searchResults.length === 0 ? (
                <div style={{ padding: '12px 14px', color: 'var(--text-secondary)', fontSize: '13px' }}>No users found for "{searchQuery}"</div>
              ) : (
                searchResults.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => {
                      setSelectedUser(user);
                      setSearchQuery(user.email);
                      setSearchResults([]);
                    }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '10px 14px', background: 'transparent',
                      border: 'none', borderBottom: '1px solid var(--border-color)',
                      cursor: 'pointer', color: 'var(--text-primary)',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover, rgba(0,0,0,0.04))'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: 600, fontSize: '14px' }}>{user.email}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{user.role}</div>
                  </button>
                ))
              )}
            </div>
          )}

          {/* Selected user chip */}
          {selectedUser && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', padding: '8px 12px', background: 'rgba(59,130,246,0.1)', border: '1px solid var(--accent-primary)', borderRadius: '8px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>{selectedUser.email}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{selectedUser.role}</div>
              </div>
              <button type="button" onClick={() => { setSelectedUser(null); setSearchQuery(''); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px' }}>
                <X size={16} />
              </button>
            </div>
          )}
        </div>

        <div className="cat-form-row">
          <label className="input-label" htmlFor="share-perm">Permission</label>
          <select id="share-perm" className="input-field" value={permission} onChange={(e) => setPermission(e.target.value)}>
            {PERMISSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="cat-form-row">
          <label className="input-label" htmlFor="share-expiry">Expires</label>
          <select id="share-expiry" className="input-field" value={expiryKey} onChange={(e) => setExpiryKey(e.target.value)}>
            {EXPIRY_OPTIONS.map((o) => <option key={o.label} value={o.label}>{o.label}</option>)}
          </select>
        </div>
        {error && <p className="form-error">{error}</p>}
        <div className="cat-modal-footer">
          <button type="button" className="btn-ghost" onClick={onClose}>Close</button>
          <button type="submit" className="btn-primary" disabled={loading || !selectedUser}>
            {loading ? 'Sharing…' : 'Share Category'}
          </button>
        </div>
      </form>

      {/* Current shares */}
      <div className="cat-share-list">
        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Active Shares</h3>
        {sharesLoading ? <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</p> :
          shares?.length === 0 ? <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>No active shares</p> :
            shares?.map((s) => (
              <div key={s.id} className="cat-share-row">
                <div>
                  <span className="cat-share-recipient">{s.recipient_email || s.recipient_team_name}</span>
                  <span className="cat-share-meta">{s.permission} · <StatusBadge status={s.effective_status} /></span>
                </div>
                {s.effective_status === 'ACTIVE' && (
                  <button className="icon-button" onClick={() => handleRevoke(s.id)} title="Revoke">
                    <X size={14} />
                  </button>
                )}
              </div>
            ))
        }
      </div>
    </Modal>
  );
}


// ─── Category Card ────────────────────────────────────────────────────────────

function CategoryCard({ category, onEdit, onDelete, onShare, viewMode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const isSystem = category.is_system;
  const iconBg = `${category.color}22`;

  if (viewMode === 'list') {
    return (
      <div className="cat-list-row">
        <div className="cat-list-icon" style={{ background: iconBg }}>
          <CategoryIcon icon={category.icon} color={category.color} size={18} />
        </div>
        <div className="cat-list-info">
          <strong onClick={() => navigate(`/categories/${category.id}`)} style={{ cursor: 'pointer' }}>
            {category.name}
          </strong>
          {category.description && <span className="cat-list-desc">{category.description}</span>}
        </div>
        <div className="cat-list-stats">
          <span title="Items"><Lock size={12} /> {category.item_count || 0}</span>
          <span title="Favorites"><Star size={12} /> {category.favorite_count || 0}</span>
          {category.share_count > 0 && <span title="Shares"><Share2 size={12} /> {category.share_count}</span>}
        </div>
        <div className="cat-list-actions">
          <Link to={`/categories/${category.id}`} className="icon-button" title="Open"><ExternalLink size={15} /></Link>
          {!isSystem && <>
            <button className="icon-button" onClick={() => onEdit(category)} title="Edit"><Edit2 size={15} /></button>
            <button className="icon-button" onClick={() => onShare(category)} title="Share"><Share2 size={15} /></button>
            <button className="icon-button" onClick={() => onDelete(category)} title="Delete" style={{ color: 'var(--danger)' }}><Trash2 size={15} /></button>
          </>}
        </div>
      </div>
    );
  }

  return (
    <div className={`cat-card glass-panel ${menuOpen ? 'menu-active' : ''}`}>
      <div className="cat-card-header">
        <div className="cat-card-icon" style={{ background: iconBg }}>
          <CategoryIcon icon={category.icon} color={category.color} size={22} />
        </div>
        {!isSystem && (
          <div className={`action-menu-wrap ${menuOpen ? 'open' : ''}`}>
            <button className="icon-button" onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }} aria-label="More actions">
              <MoreHorizontal size={16} />
            </button>
            {menuOpen && (
              <>
                <div
                  className="action-menu-backdrop"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                  }}
                />
                <div className="action-menu" onClick={() => setMenuOpen(false)}>
                  <div className="action-menu-item-header">
                    <div className="action-menu-item-title">{category.name}</div>
                    <div className="action-menu-item-sub">Category</div>
                  </div>
                  <div className="action-menu-item-divider" />
                  <button onClick={() => navigate(`/categories/${category.id}`)}><ExternalLink size={14} /> Open</button>
                  <button onClick={() => onEdit(category)}><Edit2 size={14} /> Edit</button>
                  <button onClick={() => onShare(category)}><Share2 size={14} /> Share</button>
                  <button onClick={() => onDelete(category)} className="danger-action"><Trash2 size={14} /> Delete</button>
                  <div className="action-menu-cancel-row">
                    <button type="button" className="action-menu-cancel-btn" onClick={() => setMenuOpen(false)}>Cancel</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {isSystem && <span className="cat-badge badge-system">System</span>}
      </div>

      <h3 className="cat-card-name" onClick={() => navigate(`/categories/${category.id}`)} style={{ cursor: 'pointer' }}>
        {category.name}
      </h3>
      {category.description && <p className="cat-card-desc">{category.description}</p>}

      <div className="cat-card-stats">
        <div className="cat-stat"><Lock size={13} /><span>{category.item_count || 0} items</span></div>
        {(category.favorite_count || 0) > 0 && <div className="cat-stat"><Star size={13} /><span>{category.favorite_count} fav</span></div>}
        {(category.share_count || 0) > 0 && <div className="cat-stat"><Share2 size={13} /><span>{category.share_count} shared</span></div>}
      </div>

      <div className="cat-card-footer">
        <Link to={`/categories/${category.id}`} className="text-link" style={{ fontSize: '13px' }}>
          Open <ExternalLink size={12} />
        </Link>
        <button className="icon-button" onClick={() => onShare(category)} title="Share"><Share2 size={14} /></button>
      </div>

    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('system');
  const [filter, setFilter] = useState('all'); // 'all' | 'personal' | 'system' | 'team'
  const [modal, setModal] = useState(null); // null | {type, data}
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await categoriesApi.getAll();
      setCategories(data);
    } catch {
      setError('Failed to load categories. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const filtered = useMemo(() => {
    let list = [...categories];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
    }

    if (filter === 'personal') list = list.filter((c) => !c.is_system && !c.team_id);
    else if (filter === 'system') list = list.filter((c) => c.is_system);
    else if (filter === 'team') list = list.filter((c) => !!c.team_id);

    if (sort === 'az') list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === 'za') list.sort((a, b) => b.name.localeCompare(a.name));
    else if (sort === 'items') list.sort((a, b) => (b.item_count || 0) - (a.item_count || 0));
    else if (sort === 'updated') list.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    else {
      // Default: system first, then sort_order
      list.sort((a, b) => {
        if (a.is_system && !b.is_system) return -1;
        if (!a.is_system && b.is_system) return 1;
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
    }
    return list;
  }, [categories, search, sort, filter]);

  const openModal = (type, data = null) => { setActionError(''); setModal({ type, data }); };
  const closeModal = () => { setModal(null); setActionError(''); };

  const handleCreate = async (dto) => {
    setActionLoading(true);
    setActionError('');
    try {
      const created = await categoriesApi.create(dto);
      setCategories((prev) => [created, ...prev]);
      closeModal();
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to create category');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = async (dto) => {
    setActionLoading(true);
    setActionError('');
    try {
      const updated = await categoriesApi.update(modal.data.id, dto);
      setCategories((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      closeModal();
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to update category');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (opts) => {
    setActionLoading(true);
    setActionError('');
    try {
      const result = await categoriesApi.delete(modal.data.id, opts);
      if (result?.requiresAction) {
        setActionError(`This category has ${result.itemCount} items. Choose what to do with them.`);
        return;
      }
      setCategories((prev) => prev.filter((c) => c.id !== modal.data.id));
      closeModal();
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to delete category');
    } finally {
      setActionLoading(false);
    }
  };

  const handleShare = async (dto) => {
    setActionLoading(true);
    setActionError('');
    try {
      // Look up user by email via search API
      const { data: users } = await api.get('/users/search', { params: { email: dto.recipientEmail } })
        .catch(() => ({ data: [] }));

      const recipientUserId = Array.isArray(users) ? users[0]?.id : users?.id;
      if (!recipientUserId) {
        setActionError('User not found with this email address');
        setActionLoading(false);
        return;
      }

      await categoriesApi.share(modal.data.id, {
        recipientUserId,
        permission: dto.permission,
        expiresAt: dto.expiresAt,
      });

      await fetchCategories();
      setActionError('');
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to share category');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <MainLayout>
      {/* Page Header */}
      <div className="page-header cat-page-header notes-hero-banner">
        <div className="notes-hero-content">
          <div>
            <h1 className="notes-hero-title">Categories</h1>
            <p className="notes-hero-subtitle">
              Organize your encrypted vault with custom folders and classifications.
            </p>
          </div>
          <button className="btn-primary notes-hero-btn-primary" onClick={() => openModal('create')} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={16} /> New Category
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="cat-toolbar cat-desktop-toolbar">
        <div className="cat-search-wrap">
          <Search size={16} color="var(--text-secondary)" />
          <input
            className="cat-search-input"
            placeholder="Search categories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search categories"
          />
        </div>

        <div className="cat-toolbar-right">
          <div className="cat-select-wrap">
            <ArrowUpDown size={14} />
            <select className="input-field cat-select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort categories">
              <option value="system">Default</option>
              <option value="az">A → Z</option>
              <option value="za">Z → A</option>
              <option value="items">Most Items</option>
              <option value="updated">Recently Updated</option>
            </select>
          </div>

          <div className="cat-filter-tabs" role="tablist">
            {['all', 'personal', 'system', 'team'].map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={filter === f}
                className={`cat-filter-tab ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          <div className="view-toggle">
            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')} aria-label="Grid view"><Grid size={15} /></button>
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')} aria-label="List view"><LayoutList size={15} /></button>
          </div>
        </div>
      </div>

      {/* Mobile Single-Row Filter Bar */}
      <div className="cat-mobile-filter-bar">
        <button
          type="button"
          className={`cat-mobile-icon-btn ${search || isMobileSearchOpen ? 'is-active' : ''}`}
          onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
          title="Search"
          aria-label="Search categories"
        >
          <Search size={17} />
          {search && <span className="cat-filter-dot" />}
        </button>
        <div
          className={`cat-mobile-icon-btn ${filter !== 'all' ? 'is-active' : ''}`}
          title="Filter categories"
        >
          <Folder size={17} />
          {filter !== 'all' && <span className="cat-filter-dot" />}
          <select
            className="cat-filter-hidden-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter category type"
          >
            <option value="all">All types</option>
            <option value="personal">Personal</option>
            <option value="system">System</option>
            <option value="team">Team</option>
          </select>
        </div>
        <div
          className={`cat-mobile-icon-btn ${sort !== 'system' ? 'is-active' : ''}`}
          title="Sort"
        >
          <ArrowUpDown size={17} />
          <select
            className="cat-filter-hidden-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort categories"
          >
            <option value="system">Default</option>
            <option value="az">A → Z</option>
            <option value="za">Z → A</option>
            <option value="items">Most Items</option>
            <option value="updated">Recently Updated</option>
          </select>
        </div>
        <div className="cat-mobile-view-toggle">
          <button
            type="button"
            className={viewMode === 'grid' ? 'active' : ''}
            onClick={() => setViewMode('grid')}
            aria-label="Grid view"
          >
            <Grid size={15} />
          </button>
          <button
            type="button"
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => setViewMode('list')}
            aria-label="List view"
          >
            <LayoutList size={15} />
          </button>
        </div>
        {(search || filter !== 'all' || sort !== 'system') && (
          <button
            type="button"
            className="cat-mobile-icon-btn cat-mobile-reset-btn"
            onClick={() => {
              setSearch('');
              setFilter('all');
              setSort('system');
              setIsMobileSearchOpen(false);
            }}
            title="Clear filters"
            aria-label="Clear filters"
          >
            <X size={15} />
          </button>
        )}
      </div>
      {isMobileSearchOpen && (
        <div className="cat-mobile-search-expand">
          <Search size={15} className="cat-mobile-search-inner-icon" />
          <input
            className="cat-mobile-search-input"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories…"
            autoFocus
            autoComplete="off"
            aria-label="Search categories mobile"
          />
          <button
            type="button"
            className="cat-mobile-search-close"
            onClick={() => {
              setSearch('');
              setIsMobileSearchOpen(false);
            }}
            aria-label="Close search"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="vault-loading-state"><div className="loading-spinner" /> Loading categories…</div>
      ) : error ? (
        <div className="vault-error-state"><AlertTriangle size={18} />{error}</div>
      ) : filtered.length === 0 ? (
        <div className="vault-empty-state glass-panel">
          <div className="vault-empty-icon"><Folder size={28} /></div>
          <h3>{search ? 'No matching categories' : 'No categories yet'}</h3>
          <p>{search ? 'Try a different search term.' : 'Create categories to organize your encrypted vault.'}</p>
          {!search && (
            <button className="btn-primary" onClick={() => openModal('create')} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Plus size={16} /> Create Category
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="cat-grid">
          {filtered.map((cat) => (
            <CategoryCard
              key={cat.id}
              category={cat}
              viewMode="grid"
              onEdit={(c) => openModal('edit', c)}
              onDelete={(c) => openModal('delete', c)}
              onShare={(c) => openModal('share', c)}
            />
          ))}
        </div>
      ) : (
        <div className="glass-panel cat-list-panel">
          {filtered.map((cat) => (
            <CategoryCard
              key={cat.id}
              category={cat}
              viewMode="list"
              onEdit={(c) => openModal('edit', c)}
              onDelete={(c) => openModal('delete', c)}
              onShare={(c) => openModal('share', c)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {modal?.type === 'create' && (
        <Modal title="New Category" onClose={closeModal}>
          <CategoryForm onSubmit={handleCreate} onClose={closeModal} loading={actionLoading} error={actionError} />
        </Modal>
      )}

      {modal?.type === 'edit' && (
        <Modal title="Edit Category" onClose={closeModal}>
          <CategoryForm initial={modal.data} onSubmit={handleEdit} onClose={closeModal} loading={actionLoading} error={actionError} />
        </Modal>
      )}

      {modal?.type === 'delete' && (
        <DeleteModal
          category={modal.data}
          categories={categories.filter((c) => !c.is_system || c.id !== modal.data.id)}
          onClose={closeModal}
          onConfirm={handleDelete}
          loading={actionLoading}
          error={actionError}
        />
      )}

      {modal?.type === 'share' && (
        <ShareModal
          category={modal.data}
          onClose={closeModal}
          onShare={handleShare}
          loading={actionLoading}
          error={actionError}
        />
      )}
    </MainLayout>
  );
}
